import { PRECIO_ANUAL_NUM, MONEDA } from "../config/precios.js";

const TRACKING_ID = 'G-V6QEKW1TQG';
const CONSENT_KEY = 'wayo_cookie_consent';

let analyticsLoaded = false;

export const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;

  return window.localStorage.getItem(CONSENT_KEY) === 'accepted';
};

export const loadGoogleAnalytics = () => {
  if (typeof window === 'undefined' || analyticsLoaded || !hasAnalyticsConsent()) {
    return;
  }

  analyticsLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING_ID}`;
  document.head.appendChild(script);
};

export const logPageView = (path) => {
  if (typeof window !== 'undefined' && hasAnalyticsConsent()) {
    loadGoogleAnalytics();
    window.gtag('config', TRACKING_ID, {
      page_path: path,
      anonymize_ip: true,
    });
    console.log(`📊 Page view: ${path}`);
  }
};

export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && hasAnalyticsConsent()) {
    loadGoogleAnalytics();
    window.gtag('event', eventName, {
      ...params,
      transport_type: 'beacon',
    });
    console.log(`GA4 event: ${eventName}`, params);
  } else {
    console.log('gtag no disponible');
  }
};

export const logEvent = (action, category, label = '', value = 0) => {
  trackEvent(action, {
    event_category: category,
    event_label: label,
    value,
  });
};

// ===== EVENTOS ESPECÍFICOS DE WAYO =====

export const trackNewsletterSignup = (source) => {
  logEvent('signup', 'Newsletter', source);
};

export const trackWaitlistSignup = (source) => {
  logEvent('signup', 'Waitlist', source);
};

export const trackEbookDownload = () => {
  logEvent('download', 'Ebook', '50 recetas');
};

export const trackCTAClick = (cta_name) => {
  logEvent('click', 'CTA', cta_name);
};

export const trackBlogView = (title) => {
  logEvent('view', 'Blog', title);
};

export const trackSocialClick = (platform) => {
  logEvent('click', 'Social', platform);
};

export const trackAppStoreClick = (source = 'unknown') => {
  trackEvent('click_app_store', { location: source });
};

export const trackGooglePlayClick = (source = 'unknown') => {
  trackEvent('click_google_play', { location: source });
};

/* Asistente Wayo en la landing.
   El embudo que interesa: se abre el chat → se prueba al menos una pregunta →
   se descarga. El clic a las tiendas ya se mide con trackAppStoreClick('asistente'),
   así que estos dos eventos cierran el embudo por delante.
   `origen` distingue la burbuja flotante ('fab', 'teaser') de la sección ('seccion'). */
export const trackAsistenteAbierto = (origen = 'unknown') => {
  trackEvent('asistente_abierto', { origen });
};

export const trackAsistentePregunta = (pregunta, origen = 'unknown') => {
  trackEvent('asistente_pregunta', { pregunta, origen });
};

export const trackCoachFormSubmit = (source = 'unknown') => {
  logEvent('coach_form_submit', 'Coach', source);
};

export const trackCoachCtaClick = (source = 'unknown') => {
  logEvent('coach_cta_click', 'Coach', source);
};

export const trackBlogCtaClick = (source = 'unknown') => {
  logEvent('blog_cta_click', 'Blog', source);
};

// Embudo de compra web (Stripe). El plan lleva 7 días de prueba, así que:
//   begin_checkout -> el usuario pulsa "Empezar" y le mandamos a Stripe
//   start_trial    -> vuelve de Stripe con la prueba iniciada (conversión, SIN ingreso)
//   purchase       -> el cobro real a los 7 días lo emite el BACKEND vía
//                     Measurement Protocol (webhook invoice.paid). Aquí NO se emite
//                     'purchase' para no contar doble el ingreso.
// ⚠️ El valor por defecto era 49.99 A FUEGO y los dos sitios que llaman a estas
// funciones lo hacen SIN pasar importe, asi que GA4 llevaba midiendo todo el
// embudo al precio viejo. Ahora sale del precio central.
export const trackBeginCheckout = ({ value = PRECIO_ANUAL_NUM, currency = MONEDA } = {}) => {
  trackEvent('begin_checkout', {
    value,
    currency,
    items: [
      {
        item_id: 'wayo_premium_annual',
        item_name: 'Wayo Premium Anual',
        price: value,
        quantity: 1,
      },
    ],
  });
};

export const trackStartTrial = ({ transactionId, value = PRECIO_ANUAL_NUM, currency = MONEDA } = {}) => {
  // Evento NO monetizado: marca la conversión de prueba para atribución por canal.
  // El ingreso real llega después como 'purchase' desde el backend.
  trackEvent('start_trial', {
    transaction_id: transactionId,
    value,
    currency,
  });
};

// Devuelve el client_id de GA (del cookie _ga: "GA1.1.<clientId>") para poder
// atribuir la compra real que el backend enviará por Measurement Protocol.
// null si no hay cookie (p.ej. sin consentimiento de cookies).
export const getGaClientId = () => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  // Formato: GA1.1.1234567890.1234567890  -> client_id = "1234567890.1234567890"
  const parts = match[1].split('.');
  return parts.length >= 4 ? parts.slice(-2).join('.') : null;
};
